import { useCallback, useEffect, useRef } from "react";
import { sendMessageStreaming } from "@/api/chat";
import { downloadReport } from "@/api/chat";
import { renderMarkdown } from "@/utils/markdown";
import {
  extractTitle,
  parseChartMarkers,
  triggerDownload,
} from "../utils/reportUtils";
import { useChatStore } from "../stores/chat";
import { bulkUploadApi } from "@/features/analytics/api/analyticsService";
import type {
  ChatMessage,
  FileAttachment,
  FormContext,
  ScreenContext,
} from "@/types";

const STREAM_RENDER_INTERVAL = 200;
// The panel and full chat share one conversation store and one active request.
let activeStream: {
  owner: object;
  abort: () => void;
  detach: () => void;
} | null = null;
const abortActiveStream = () => activeStream?.abort();
const detachActiveStream = () => activeStream?.detach();

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => {
    const entities: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "'": "&#39;",
      '"': "&quot;",
    };
    return entities[character];
  });
}

interface StreamCallbacks {
  onReviewReady?: (batchId: string, recordCount: number) => void;
  onResult?: (chatId: string) => void;
  onPendingReview?: (chatId: string, messageId: number) => void;
}

export function useStreaming() {
  const renderTimerRef = useRef<number | undefined>(undefined);
  const streamRenderRequestIdRef = useRef(0);
  const streamingRawRef = useRef("");
  const lastRenderRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const streamSessionRef = useRef(0);
  const cancelUploadRef = useRef<(() => void) | null>(null);

  const {
    addMessage,
    completeStreamingMessage,
    updateLastUserMessage,
    setStreamingContent,
    setIsThinking,
    setIsStreaming,
    setCurrentProgress,
    setChatId,
  } = useChatStore.getState();

  const cleanup = useCallback(() => {
    streamRenderRequestIdRef.current += 1;
    if (renderTimerRef.current) {
      clearTimeout(renderTimerRef.current);
      renderTimerRef.current = undefined;
    }
  }, []);

  const flushRender = useCallback(() => {
    lastRenderRef.current = Date.now();
    const requestId = ++streamRenderRequestIdRef.current;
    void renderMarkdown(streamingRawRef.current)
      .then((html) => {
        if (requestId === streamRenderRequestIdRef.current) {
          setStreamingContent(html);
        }
      })
      .catch(() => {});
  }, [setStreamingContent]);

  const abortOwn = useCallback(() => {
    if (activeStream?.owner === abortRef) activeStream = null;
    streamSessionRef.current += 1;
    cancelUploadRef.current?.();
    cleanup();
    setStreamingContent(null);
    setIsThinking(false);
    setIsStreaming(false);
    setCurrentProgress(null);
  }, [
    cleanup,
    setCurrentProgress,
    setIsThinking,
    setIsStreaming,
    setStreamingContent,
  ]);

  const detachOwn = useCallback(() => {
    if (activeStream?.owner === abortRef) activeStream = null;
    streamSessionRef.current += 1;
    abortRef.current?.abort();
    cleanup();
    setStreamingContent(null);
    setIsThinking(false);
    setIsStreaming(false);
    setCurrentProgress(null);
  }, [
    cleanup,
    setCurrentProgress,
    setIsThinking,
    setIsStreaming,
    setStreamingContent,
  ]);

  const sendMessage = useCallback(
    async (
      message: string,
      token: string,
      options: {
        chatId?: string;
        businessUnitId?: string;
        businessUnitName?: string;
        logEmissionEnabled?: boolean;
        files?: FileAttachment[];
        pageContext?: {
          page: string;
          filters?: Record<string, unknown>;
          screenContext?: ScreenContext;
        };
        formContext?: FormContext;
      },
      callbacks?: StreamCallbacks,
    ) => {
      activeStream?.detach();
      activeStream = { owner: abortRef, abort: abortOwn, detach: detachOwn };
      abortRef.current?.abort();
      const streamSession = ++streamSessionRef.current;
      const generation = useChatStore.getState().chatGeneration;
      const isCurrent = () =>
        streamSessionRef.current === streamSession &&
        generation === useChatStore.getState().chatGeneration;
      cleanup();
      streamingRawRef.current = "";
      setStreamingContent(null);
      lastRenderRef.current = 0;
      setIsThinking(true);
      setIsStreaming(true);
      setCurrentProgress(null);

      let pendingReviewBatch: { batchId: string; recordCount: number } | null =
        null;
      let terminalEventReceived = false;

      const controller = new AbortController();
      abortRef.current = controller;
      const jobIds = new Set<number>();
      let stopRequested = false;
      const cancelUpload = (jobId: number) => {
        void bulkUploadApi.cancelJob(jobId, "Stopped from chat").catch(() => {
          addMessage({
            role: "assistant",
            isError: true,
            content:
              "Upload cancellation could not be confirmed. Check Upload Activity before starting another upload.",
          });
        });
      };
      cancelUploadRef.current = () => {
        stopRequested = true;
        for (const jobId of jobIds) cancelUpload(jobId);
        // Keep a file request attached until its admission receipt arrives, so Stop
        // can cancel even when clicked while the server is checking the upload.
        if (
          jobIds.size > 0 ||
          !options.logEmissionEnabled ||
          !options.files?.length
        )
          controller.abort();
      };

      try {
        await sendMessageStreaming(
          {
            message,
            chatId: options.chatId,
            businessUnitId: options.businessUnitId,
            businessUnitName: options.businessUnitName,
            logEmissionEnabled: options.logEmissionEnabled,
            files: options.files,
            pageContext: options.pageContext,
            formContext: options.formContext,
          },
          token,
          {
            onToolStep() {},
            onAccepted(id) {
              if (!isCurrent()) return;
              setChatId(id);
            },
            onUploadSubmitted(jobId, id) {
              jobIds.add(jobId);
              if (stopRequested) {
                cancelUpload(jobId);
                controller.abort();
                return;
              }
              if (!isCurrent()) return;
              setChatId(id);
            },
            onProgress(msg) {
              if (!isCurrent()) return;
              setCurrentProgress(msg);
            },
            onToken(text) {
              if (!isCurrent() || terminalEventReceived) return;
              streamingRawRef.current += text;
              setIsThinking(false);

              const now = Date.now();
              if (now - lastRenderRef.current >= STREAM_RENDER_INTERVAL) {
                lastRenderRef.current = now;
                const requestId = ++streamRenderRequestIdRef.current;
                void renderMarkdown(streamingRawRef.current)
                  .then((html) => {
                    if (requestId === streamRenderRequestIdRef.current) {
                      setStreamingContent(html);
                    }
                  })
                  .catch(() => {});
              }

              if (renderTimerRef.current) clearTimeout(renderTimerRef.current);
              renderTimerRef.current = setTimeout(
                flushRender,
                STREAM_RENDER_INTERVAL,
              );
            },
            onFileBubble(files) {
              if (!isCurrent()) return;
              updateLastUserMessage((msg) => ({
                ...msg,
                fileAttachments: files,
              }));
            },
            onReviewReady(batchId, recordCount) {
              if (!isCurrent()) return;
              pendingReviewBatch = { batchId, recordCount };
            },
            onResult(text, id, isReport, autoDownload, pendingCreateId) {
              if (!isCurrent() || terminalEventReceived) return;
              terminalEventReceived = true;
              cleanup();
              streamRenderRequestIdRef.current += 1;
              if (text.startsWith("{")) {
                try {
                  const parsed = JSON.parse(text);
                  if (typeof parsed.text === "string") text = parsed.text;
                } catch {
                  /* noop */
                }
              }

              const { cleanText, charts } = parseChartMarkers(text);

              const finalize = (renderedContent: string) => {
                if (!isCurrent()) return;
                const assistantMsg: ChatMessage = {
                  role: "assistant",
                  content: renderedContent,
                  rawContent: cleanText,
                  charts: charts.length > 0 ? charts : undefined,
                  isReport,
                  autoDownload: autoDownload as ChatMessage["autoDownload"],
                  reviewBatch: pendingReviewBatch ?? undefined,
                  pendingEmission:
                    pendingCreateId && !pendingReviewBatch
                      ? {
                          chatId: id,
                          messageId: pendingCreateId,
                          status: "pending_create",
                        }
                      : undefined,
                };
                completeStreamingMessage(assistantMsg);
                pendingReviewBatch = null;

                if (id) {
                  setChatId(id);
                  callbacks?.onResult?.(id);
                }
                if (
                  assistantMsg.pendingEmission &&
                  id &&
                  !options.files?.length
                )
                  callbacks?.onPendingReview?.(
                    id,
                    assistantMsg.pendingEmission.messageId,
                  );
                if (
                  autoDownload &&
                  typeof autoDownload === "object" &&
                  "format" in autoDownload &&
                  (autoDownload.format === "pdf" ||
                    autoDownload.format === "docx")
                ) {
                  const download = autoDownload as {
                    format: "pdf" | "docx";
                    title?: string;
                    content?: string;
                  };
                  void triggerDownload(
                    download.content || cleanText,
                    download.title || extractTitle(cleanText),
                    download.format,
                    (content, title, authToken) =>
                      downloadReport(
                        content,
                        title,
                        authToken,
                        download.format,
                      ),
                    token,
                  ).catch(() => {});
                }
              };

              void renderMarkdown(cleanText)
                .then((html) => {
                  finalize(html);
                })
                .catch(() => {
                  const safeText = escapeHtml(cleanText).replace(
                    /\n/g,
                    "<br />",
                  );
                  finalize(safeText);
                });
            },
            onError(msg) {
              if (!isCurrent()) return;
              terminalEventReceived = true;
              streamSessionRef.current += 1;
              cleanup();
              setStreamingContent(null);
              addMessage({
                role: "assistant",
                content: `Error: ${msg}`,
                isError: true,
              });
              setIsThinking(false);
              setCurrentProgress(null);
              setIsStreaming(false);
            },
          },
          controller.signal,
        );
        if (!isCurrent()) return;
        if (!terminalEventReceived) {
          cleanup();
          setStreamingContent(null);
          addMessage({
            role: "assistant",
            content:
              jobIds.size > 0
                ? "The connection ended. Your upload continues in Upload Activity; check it before starting another upload."
                : "Error: The response ended before completion. Please try again.",
            isError: true,
          });
          setIsThinking(false);
          setCurrentProgress(null);
          setIsStreaming(false);
        }
      } catch (err) {
        if (!isCurrent()) return;
        cleanup();
        if (err instanceof DOMException && err.name === "AbortError") {
          setStreamingContent(null);
          setIsThinking(false);
          setCurrentProgress(null);
          setIsStreaming(false);
          return;
        }
        setStreamingContent(null);
        addMessage({
          role: "assistant",
          content:
            jobIds.size > 0
              ? "The connection ended. Your upload continues in Upload Activity; check it before starting another upload."
              : `Error: ${(err as Error).message}`,
          isError: true,
        });
        setIsThinking(false);
        setCurrentProgress(null);
        setIsStreaming(false);
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          cancelUploadRef.current = null;
        }
      }
    },
    [
      cleanup,
      flushRender,
      abortOwn,
      detachOwn,
      addMessage,
      completeStreamingMessage,
      updateLastUserMessage,
      setStreamingContent,
      setIsThinking,
      setIsStreaming,
      setCurrentProgress,
      setChatId,
    ],
  );

  useEffect(
    () => () => {
      // Unmounting an idle surface must not stop the other surface's request.
      if (activeStream?.owner === abortRef) detachOwn();
      else cleanup();
    },
    [cleanup, detachOwn],
  );

  return {
    sendMessage,
    abort: abortActiveStream,
    detach: detachActiveStream,
    cleanup,
  };
}
